import api from '@cannect/services/api'
import { TResponseStandard, TResponseStandardWithPagination } from '@cannect/services/types'
import { TProductDetailItem, TProductFilters, TProductMedicalData, TProductSubcategory } from './types'

export const getProductById = async (productId: number) => {
  return api
    .get<
      TResponseStandard<TProductDetailItem, 'product'> & {
        medicalData: TProductMedicalData
      }
    >(`product/${productId}`)
    .then(({ data }) => data)
}

export const getProductsFilters = async () => {
  return api.get<TResponseStandard<TProductFilters, 'filters'>>('/product-filters').then(({ data }) => data)
}

export const getPublicProductsList = async (
  page: string,
  queryParams: URLSearchParams
): Promise<TResponseStandardWithPagination<TProductDetailItem[], 'products'>> => {
  const updatedParams = new URLSearchParams(queryParams)
  updatedParams.set('page', queryParams.get('page') || '1') // default page is 1
  updatedParams.set('limit', queryParams.get('limit') || '12') // default limit is 12
  return api
    .get<
      TResponseStandardWithPagination<TProductDetailItem[], 'products'>
    >(`/list-products-public?${updatedParams.toString()}`)
    .then(({ data }) => data)
}

export const getAuthenticatedProductsList = async (page: string, queryParams: URLSearchParams) => {
  const updatedParams = new URLSearchParams(queryParams)
  updatedParams.set('page', queryParams.get('page') || '1') // default page is 1
  updatedParams.set('limit', queryParams.get('limit') || '12') // default limit is 12
  return api
    .get<
      TResponseStandardWithPagination<TProductDetailItem[], 'products'>
    >(`/list-products?${updatedParams.toString()}`)
    .then(({ data }) => data)
}

export const getProductsByBenefit = async (benefitCategory: string, page: string, queryParams?: URLSearchParams) => {
  const updatedParams = new URLSearchParams(queryParams)
  updatedParams.set('limit', queryParams?.get('limit') || '12')
  updatedParams.set('page', page)

  return api
    .get<
      TResponseStandardWithPagination<TProductDetailItem[], 'products'>
    >(`/list-products?category_slug=${benefitCategory}&disable_filters=true&${updatedParams.toString()}`)
    .then(({ data }) => data)
}

export const getProductsByBenefitPublic = async (
  benefitCategory: string,
  page: string,
  queryParams?: URLSearchParams
) => {
  const updatedParams = new URLSearchParams(queryParams)
  updatedParams.set('limit', queryParams?.get('limit') || '12')
  updatedParams.set('page', page)

  return api
    .get<
      TResponseStandardWithPagination<TProductDetailItem[], 'products'>
    >(`/list-products-public?category_slug=${benefitCategory}&disable_filters=true&${updatedParams.toString()}`)
    .then(({ data }) => data)
}

export const getWeeklyHighlights = async (limit: number = 8) => {
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products?order_by=bestseller&disable_filters=true&limit=${limit}`)
    .then(({ data }) => data)
}

export const getWeeklyHighlightsPublic = async (limit: number = 8, options: { onlySellable?: boolean } = {}) => {
  const onlySellableParam = options?.onlySellable ? '&only_sellable=true' : ''
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products-public?order_by=bestseller&disable_filters=true&limit=${limit}${onlySellableParam}`)
    .then(({ data }) => data)
}

export const getMostSearchedProducts = async (limit: number = 8) => {
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products?order_by=mostpopular&disable_filters=true&limit=${limit}`)
    .then(({ data }) => data)
}

export const getMostSearchedProductsPublic = async (limit: number = 8) => {
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products-public?order_by=mostpopular&disable_filters=true&limit=${limit}`)
    .then(({ data }) => data)
}

export const getProductsBySupplier = async (supplierId: number, limit: number = 8) => {
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products?supplier_id=${supplierId}&disable_filters=true&limit=${limit}`)
    .then(({ data }) => data)
}

export const getProductsBySupplierPublic = async (supplierId: number, limit: number = 8) => {
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products-public?supplier_id=${supplierId}&disable_filters=true&limit=${limit}`)
    .then(({ data }) => data)
}

export const getProductsByClass = async (classId: number, limit: number = 8) => {
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products?class_id=${classId}&disable_filters=true&limit=${limit}`)
    .then(({ data }) => data)
}

export const getProductsByClassPublic = async (
  classId: number,
  limit: number = 8,
  options: { onlySellable?: boolean } = {}
) => {
  const onlySellableParam = options?.onlySellable ? '&only_sellable=true' : ''
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products-public?class_id=${classId}&disable_filters=true&limit=${limit}${onlySellableParam}`)
    .then(({ data }) => data)
}

export const getProductsByTagPublic = async (
  tag: string,
  limit: number = 8,
  options: { onlySellable?: boolean } = {}
) => {
  const onlySellableParam = options?.onlySellable ? '&only_sellable=true' : ''
  return api
    .get<
      TResponseStandard<TProductDetailItem[], 'products'>
    >(`/list-products-public?shopify_tag=${encodeURIComponent(tag)}&disable_filters=true&limit=${limit}${onlySellableParam}`)
    .then(({ data }) => data)
}

export const toggleProductFavorite = async (productId: number) => {
  return api.post(`/product/favorite/${productId}`).then(({ data }) => data)
}

export const getProductSubcategories = async () => {
  return api
    .get<TResponseStandard<TProductSubcategory[], 'subcategories'>>('/list-product-subcategories')
    .then(({ data }) => data)
}
